import { Box, Button, HStack, Table, Tbody, Text, Th, Thead, Tr, VStack } from 'design-system';
import { useGetDealCategoryTreeApi } from 'apis/supplier-prices';
import type { MasterProductFamily } from 'apis/master-product-families';
import { useBulkUpsertMasterProductFamiliesApi } from 'apis/master-product-families';
import { EditableFamilyRowView } from './editable-family-row';
import { useEditableFamiliesRows } from './use-editable-families-rows';

interface Props {
  families: MasterProductFamily[];
  onDone: () => void; // exit edit mode
}

const HEADERS = ['Universal Name', 'Category', ''];

export const EditableFamiliesGrid = ({ families, onDone }: Props) => {
  const { rows, errors, dirty, updateRow, insert, remove, append, prepend, buildPayload } = useEditableFamiliesRows(families);
  const { mutate: bulkUpsert, isLoading, error: saveError } = useBulkUpsertMasterProductFamiliesApi();
  const { data: categoryTreeData } = useGetDealCategoryTreeApi();
  const categoryTree = categoryTreeData?.data ?? [];

  const errorByClientId = new Map(errors.map((e) => [e.clientId, e.messages]));
  const serverErrors = saveError?.response?.data?.errors ?? [];

  const onSave = () => {
    if (errors.length > 0) return;
    const changedRows = buildPayload();
    if (changedRows.length === 0) return onDone();
    bulkUpsert({ rows: changedRows }, { onSuccess: () => onDone() });
  };

  const onCancel = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    onDone();
  };

  return (
    <VStack align='stretch' spacing='md'>
      <HStack justify='space-between' py='sm' px='xs'>
        <HStack spacing='md'>
          <Button size='sm' variant='outline' onClick={prepend}>
            Add row to top
          </Button>
          <Button size='sm' variant='outline' onClick={append}>
            Add row to bottom
          </Button>
        </HStack>
        <HStack spacing='sm'>
          <Button size='sm' variant='ghost' colorScheme='gray' onClick={onCancel}>
            Cancel
          </Button>
          <Button size='sm' colorScheme='red' isLoading={isLoading} isDisabled={errors.length > 0} onClick={onSave}>
            Save
          </Button>
        </HStack>
      </HStack>

      {errors.length > 0 && (
        <Box bg='red.50' borderRadius='md' p='sm'>
          <Text fontSize='sm' color='red.600' fontWeight='semibold'>
            Fix {errors.length} row(s) before saving:
          </Text>
          {errors.map((e) => (
            <Text key={e.clientId} fontSize='sm' color='red.600'>
              Row {e.rowIndex + 1}: {e.messages.join(', ')}
            </Text>
          ))}
        </Box>
      )}

      {serverErrors.length > 0 && (
        <Box bg='red.50' borderRadius='md' p='sm'>
          <Text fontSize='sm' color='red.600' fontWeight='semibold'>
            Save failed — fix these and try again:
          </Text>
          {serverErrors.map((msg, i) => (
            <Text key={i} fontSize='sm' color='red.600'>
              {msg}
            </Text>
          ))}
        </Box>
      )}

      <Box overflowX='auto' overflowY='auto' maxH='65vh' border='1px solid' borderColor='gray.200' borderRadius='md'>
        <Table
          size='sm'
          sx={{
            'th, td': { borderWidth: '1px', borderColor: 'gray.200', px: '1.5', py: '1.5', verticalAlign: 'middle' },
            th: {
              bg: 'gray.50',
              textTransform: 'none',
              fontSize: 'xs',
              letterSpacing: 'normal',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              whiteSpace: 'nowrap',
            },
            'tbody tr:hover td': { bg: 'blackAlpha.50' },
          }}>
          <Thead>
            <Tr>
              {HEADERS.map((h, i) => (
                <Th key={i}>{h}</Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, index) => (
              <EditableFamilyRowView
                key={row.clientId}
                row={row}
                index={index}
                invalid={errorByClientId.has(row.clientId)}
                categoryTree={categoryTree}
                onChange={updateRow}
                onInsert={insert}
                onRemove={remove}
              />
            ))}
          </Tbody>
        </Table>
      </Box>
    </VStack>
  );
};
